import { useCallback, useMemo, useState } from 'react';
import { trpc } from '@org/clients/school';
import {
  Badge,
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  useAuthorization,
  useDebounce,
} from '@org/ui';
import { Eye, Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  clearImpersonatedStudent,
  readImpersonatedStudent,
  studentDisplayName,
  writeImpersonatedStudent,
  type ImpersonatedStudent,
} from '../lib/impersonation';

const MIN_SEARCH_LENGTH = 2;

/**
 * StudentPicker — lets an admin view the student app as any student.
 * Only visible to users with admin:all permission (same gate as the teacher
 * app's TeacherPicker). The choice lives in localStorage and is sent as a
 * request header by the tRPC clients (see lib/impersonation.ts); the backend
 * answers as that student and blocks writes, so the view is read-only.
 */
export function StudentPicker() {
  const { hasPermission, isLoading } = useAuthorization();
  if (isLoading || !hasPermission('admin:all')) {
    return null;
  }
  return <StudentPickerContent />;
}

// A change of identity resets every query and page state, so the simplest
// faithful switch is a fresh load of the student's home page.
const reloadAsHome = () => {
  window.location.assign('/');
};

function StudentPickerContent() {
  const { t } = useTranslation('admin');
  const current = useMemo(() => readImpersonatedStudent(), []);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search.trim(), 250);
  const canSearch = debouncedSearch.length >= MIN_SEARCH_LENGTH;

  const { data: results, isFetching } = trpc.users.searchStudents.useQuery(
    { search: debouncedSearch },
    { enabled: canSearch, placeholderData: (previous) => previous },
  );

  const choose = useCallback((student: ImpersonatedStudent) => {
    writeImpersonatedStudent(student);
    setOpen(false);
    reloadAsHome();
  }, []);

  const clear = useCallback(() => {
    clearImpersonatedStudent();
    reloadAsHome();
  }, []);

  const currentName = current ? studentDisplayName(current) : '';

  return (
    <div className="flex min-w-0 items-center gap-2">
      {current ? (
        <Badge
          variant="outline"
          title={t('p2e.layout.impersonation.readOnly')}
          className="hidden min-w-0 items-center gap-1.5 border-amber-200 bg-amber-50 px-2 py-1 text-amber-700 md:flex"
        >
          <Eye className="h-3 w-3 shrink-0" />
          <span className="truncate text-xs">{t('p2e.layout.impersonation.viewing', { name: currentName })}</span>
          <button
            type="button"
            onClick={clear}
            className="ml-1 shrink-0 rounded-full p-0.5 hover:bg-amber-200"
            title={t('p2e.layout.impersonation.clear')}
            aria-label={t('p2e.layout.impersonation.clear')}
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ) : null}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label={t('p2e.layout.impersonation.trigger')}
            className="w-40 min-w-0 justify-between border-rose-100 bg-white/80 text-sm font-normal text-gray-700 hover:bg-white sm:w-52"
          >
            <span className="truncate">{current ? currentName : t('p2e.layout.impersonation.trigger')}</span>
            <Search className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 bg-white p-0">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={t('p2e.layout.impersonation.searchPlaceholder')}
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              {!canSearch ? (
                <p className="px-3 py-3 text-xs text-gray-500">{t('p2e.layout.impersonation.typeToSearch')}</p>
              ) : isFetching && !results ? (
                <p className="px-3 py-3 text-xs text-gray-500">{t('p2e.layout.impersonation.searching')}</p>
              ) : (
                <CommandEmpty>{t('p2e.layout.impersonation.noResults')}</CommandEmpty>
              )}
              {canSearch && results?.length ? (
                <CommandGroup>
                  {results.map((student) => {
                    const name = studentDisplayName(student);
                    const initial = (name || student.email || '?').slice(0, 1).toUpperCase();
                    return (
                      <CommandItem
                        key={student.userId}
                        value={student.userId}
                        onSelect={() => choose(student)}
                        className="flex items-center gap-2"
                      >
                        {student.profileImageUrl ? (
                          <img src={student.profileImageUrl} alt="" className="h-7 w-7 shrink-0 rounded-full object-cover" />
                        ) : (
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-rose-50 text-xs font-semibold text-[#cf2e2e]">
                            {initial}
                          </span>
                        )}
                        <span className="flex min-w-0 flex-col">
                          <span className="truncate text-sm text-gray-900">{name}</span>
                          <span className="truncate text-xs text-gray-500">{student.email}</span>
                        </span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              ) : null}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
